/**
 * date.js — تنسيق التواريخ الموحّد في كل التطبيق
 *
 * صيغة العرض المعتمدة: dd/mm/yyyy بأرقام لاتينية.
 *
 * التنسيق هنا حتمي ولا يعتمد على `toLocaleDateString`، لأن الأخيرة تتبع
 * لغة المتصفح/النظام فتُخرج mm/dd/yyyy على الإنجليزية الأمريكية، وقد تُخرج
 * أرقاماً هندية أو تقويماً هجرياً على بعض اللوكيلات العربية. النظام محاسبي
 * ويجب أن يقرأ الجميع فيه التاريخ نفسه بالشكل نفسه.
 *
 * تنبيه: لا تستخدم هذه الدوال لقيم <input type="date"> ولا لحمولات الـ API؛
 * كلاهما يتطلب yyyy-mm-dd — استخدم toInputDate لذلك.
 */

/** يحوّل أي مدخل إلى كائن Date صالح، أو null إن تعذّر */
function toDate(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  // الأرقام طوابع زمنية بالمللي ثانية؛ تحويلها إلى نص يجعل 0 يُقرأ سنةً
  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }

  // "2026-08-20" و"2026-08-20 10:00:00" تُفسَّران بشكل موثوق بعد التطبيع
  const raw = String(value).trim();
  const normalized = /^\d{4}-\d{2}-\d{2}[ T]/.test(raw) ? raw.replace(' ', 'T') : raw;

  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

const pad = (n) => String(n).padStart(2, '0');

/**
 * التاريخ بصيغة dd/mm/yyyy
 * @param {Date|string|number} value
 * @param {string} [fallback='-'] ما يُعرض إذا كانت القيمة فارغة أو غير صالحة
 * @returns {string}
 */
export function formatDate(value, fallback = '-') {
  const date = toDate(value);
  if (!date) return fallback;
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/**
 * التاريخ والوقت بصيغة dd/mm/yyyy HH:mm (24 ساعة)
 * @param {Date|string|number} value
 * @param {string} [fallback='-']
 * @returns {string}
 */
export function formatDateTime(value, fallback = '-') {
  const date = toDate(value);
  if (!date) return fallback;
  return `${formatDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * التاريخ بصيغة yyyy-mm-dd — لقيم <input type="date"> وحمولات الـ API.
 * يستخدم المكوّنات المحلية لا toISOString، لأن الأخيرة تحوّل إلى UTC
 * فتُنقص يوماً كاملاً في التوقيتات الشرقية بعد الظهر.
 * @param {Date|string|number} value
 * @returns {string} سلسلة فارغة إذا تعذّر التحويل
 */
export function toInputDate(value) {
  const date = toDate(value);
  if (!date) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export default formatDate;
